
/**
 * Knowledge Base - Planned Features
 * 
 * This file documents the planned expansions for the 3DP Hub platform.
 */

export type FeatureStatus = 
  | "planned" 
  | "in-progress" 
  | "implemented";

export interface PlannedFeature {
  id: string;
  name: string;
  description: string;
  status: FeatureStatus;
  category: string;
  keyComponents?: string[];
  dependencies?: string[];
  priority?: "high" | "medium" | "low";
}

/**
 * Knowledge Base expansion features
 */
export const knowledgeBaseFeatures: PlannedFeature[] = [
  {
    id: "robust-keywords",
    name: "Robust Keyword System",
    description: "Advanced keyword tagging and organization for all content to improve searchability and connections between related items.",
    status: "planned",
    category: "knowledge-base",
    priority: "high"
  },
  {
    id: "deep-search",
    name: "Deep Search Functionality",
    description: "Powerful search engine that indexes all content and provides relevant results based on user queries with filtering and sorting options.",
    status: "planned",
    category: "knowledge-base",
    priority: "high"
  },
  {
    id: "auto-backlinking",
    name: "Automatic Backlinking System",
    description: "System that automatically creates bidirectional links between related content to improve navigation and discovery.",
    status: "planned",
    category: "knowledge-base",
    priority: "medium"
  },
  {
    id: "flowchart-help",
    name: "Flowchart Help System",
    description: "Interactive flowchart-based decision tree for guiding users through troubleshooting and part selection.",
    status: "planned",
    category: "knowledge-base",
    priority: "medium"
  },
  {
    id: "multi-language",
    name: "Multi-language Support",
    description: "Allow users to view and contribute to the site in multiple languages, making the platform accessible worldwide.",
    status: "planned",
    category: "accessibility",
    priority: "high"
  }
];

/**
 * Interactive assistance features
 */
export const interactiveFeatures: PlannedFeature[] = [
  {
    id: "diagnostic-chatbot",
    name: "Diagnostic Chatbot",
    description: "AI-powered chatbot designed to help users diagnose issues with their 3D printers and find appropriate parts or solutions.",
    status: "planned",
    category: "interactive",
    priority: "high"
  },
  {
    id: "printer-configurator",
    name: "Printer Configuration System",
    description: "Tool for users to virtually configure their printer with parts from the database and save configurations to their profile.",
    status: "planned",
    category: "interactive",
    priority: "medium"
  },
  {
    id: "3d-part-identifier",
    name: "3D Part Identifier",
    description: "An interactive tool that lets users identify parts by clicking on components within a CAD model of their printer.",
    status: "planned",
    category: "interactive",
    priority: "medium"
  }
];

/**
 * Community features
 */
export const communityFeatures: PlannedFeature[] = [
  {
    id: "user-questions",
    name: "User Question System",
    description: "Platform for users to ask questions about 3D printing issues, parts, and configurations.",
    status: "planned",
    category: "community",
    priority: "medium"
  },
  {
    id: "user-solutions",
    name: "User Solution Contributions",
    description: "System for users to suggest and share solutions to common problems or improvements to existing parts.",
    status: "planned",
    category: "community",
    priority: "medium"
  },
  {
    id: "reputation-system",
    name: "User Reputation System",
    description: "System to track user contributions and expertise through points, badges, and achievement mechanisms.",
    status: "planned",
    category: "community",
    priority: "low"
  },
  {
    id: "user-photo-gallery",
    name: "User Photo Gallery",
    description: "A gallery where users can upload and showcase images of their modified or customized 3D printers.",
    status: "planned",
    category: "community",
    priority: "medium"
  },
  {
    id: "symptom-gallery",
    name: "Symptom Gallery",
    description: "A visual library of common print issues with community-contributed images, potential causes, and suggested fixes.",
    status: "planned",
    category: "community",
    priority: "high"
  },
  {
    id: "collaborative-data-testing",
    name: "Collaborative Data Testing",
    description: "A platform for users to submit and compare testing results for things like fan performance, probe accuracy, and input shaping.",
    status: "planned",
    category: "community",
    priority: "medium"
  }
];

/**
 * All planned features combined
 */
export const allPlannedFeatures: PlannedFeature[] = [
  ...knowledgeBaseFeatures,
  ...interactiveFeatures,
  ...communityFeatures
];
